import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon, ChevronRightIcon } from 'lucide-react';
import { FilterBar } from '../components/hub/FilterBar';
import { DirectoryColumns } from '../components/hub/DirectoryColumns';
import { HubFaqs } from '../components/hub/HubFaqs';
import { SiblingHubs } from '../components/hub/SiblingHubs';
import { TierNote } from '../components/TierNote';
import { DesignNote } from '../components/DesignNote';
import { attractionsFaqs, attractionsFilters, attractionsIntro, attractionsTier3, comparison, featured, moreAttractions } from '../data/attractions';
export function Attractions() {
  const [active, setActive] = useState<string[]>([]);
  function toggle(option: string) {
    setActive(prev => prev.includes(option) ? prev.filter(x => x !== option) : [...prev, option]);
  }
  const filtered = useMemo(() => {
    if (active.length === 0) return moreAttractions;
    return moreAttractions.filter(a => active.every(f => a.tags.includes(f) || a.area === f));
  }, [active]);
  return <main id="main" className="bg-paper" data-id="element-341" data-mp="2khyssv4ce6nh">
      <div className="mx-auto max-w-shell px-6 pb-12 pt-8 lg:px-10" data-id="element-342" data-mp="2kj06768tz58d">
        <nav aria-label="Breadcrumb" data-id="element-343" data-mp="26iz2zk53flpq">
          <ol className="flex flex-wrap items-center gap-2 text-[12.5px] text-muted" data-id="element-344" data-mp="26k0hxzp8cscq">
            <li data-id="element-345" data-mp="2bhac1snkds6w">
              <Link to="/" className="hover:text-ink hover:underline" data-id="element-346" data-mp="2biv85mpj1j6v">
                Home
              </Link>
            </li>
            <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-347" data-mp="338pxvkharaw2" />
            <li data-id="element-348" data-mp="33a14ds24mwqv">
              <Link to="/things-to-do" className="hover:text-ink hover:underline" data-id="element-349" data-mp="2ph1pnsa656kd">
                Things to do
              </Link>
            </li>
            <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-350" data-mp="2uebhfel15ubd" />
            <li aria-current="page" className="text-ink" data-id="element-351" data-mp="3i4fm7p9yg03y">
              Attractions
            </li>
          </ol>
        </nav>

        <DesignNote label="Category page, one level below the hub" note="This is where /santa-barbara-family-attractions redirects. In Squarespace it is a Category on the Things to Do collection, so the URL comes free." data-id="element-352" data-mp="3mxi95ek88bb6" />

        <div className="max-w-3xl" data-id="element-353" data-mp="3mtgngr7e6nnf">
          <p className="label text-olive" data-id="element-354" data-mp="3msf31w4hkot1">
            {attractionsIntro.count} · {attractionsIntro.updated}
          </p>
          <h1 className="mt-6 font-display text-[48px] font-300 leading-[0.98] text-ink sm:text-[64px]" data-id="element-355" data-mp="3hwzt3f9lnd1w">
            Family attractions in <span className="italic" data-id="element-356" data-mp="392v3ez67c7ny">Santa Barbara</span>
          </h1>
          <p className="mt-7 max-w-prose text-[16px] leading-[1.85] text-muted" data-id="element-357" data-mp="390ige7ll7741">
            {attractionsIntro.deck}
          </p>
        </div>

        <img src={attractionsIntro.hero} alt={attractionsIntro.heroAlt} className="photo-soft mt-14 aspect-[21/9] w-full object-cover" data-id="element-358" data-mp="2z56xre725fr0" />
      </div>

      <Comparison data-id="element-359" data-mp="2q37fkikb45mh" />

      <section aria-labelledby="featured-h" className="border-t border-line bg-white" data-id="element-360" data-mp="2q4sbabicxulx">
        <div className="mx-auto max-w-shell px-6 py-20 lg:px-10 lg:py-24" data-id="element-361" data-mp="15mgbid8qekkz">
          <TierNote tier="1" label="Full place pages" note="The four with their own URL, photos and a real write-up. These are also the four a business would sponsor." data-id="element-362" data-mp="19n09tk51ju8p" />
          <h2 id="featured-h" className="max-w-2xl font-display text-[38px] font-300 leading-[1.05] text-ink sm:text-[46px]" data-id="element-363" data-mp="1jlzhp0hzflun">
            The four worth a full write-up
          </h2>

          <ul className="mt-16 grid gap-x-16 gap-y-20 lg:grid-cols-2" data-id="element-364" data-mp="qxtvsaf6zqt8">
            {featured.map(place => <li key={place.slug} className="group relative" data-id="element-365" data-mp="vuantfpiipbl">
                <div className="overflow-hidden bg-creme" data-id="element-366" data-mp="10n3o9rdy7bhv">
                  <img src={place.image} alt={place.imageAlt} className="photo-soft aspect-[4/3] w-full object-cover transition-transform duration-300 ease-soft group-hover:scale-[1.03]" data-id="element-367" data-mp="10ooith09iufq" />
                </div>
                <p className="label mt-7 text-olive" data-id="element-368" data-mp="29vqb89x997tg">{place.area}</p>
                <h3 className="mt-3 font-display text-[32px] font-300 leading-tight text-ink" data-id="element-369" data-mp="25uwnbgjtbt0k">
                  <Link to="/place" className="decoration-olive decoration-1 underline-offset-[7px] after:absolute after:inset-0 after:content-[''] group-hover:underline" data-id="element-370" data-mp="2jmbe49jyoyi6">
                    {place.name}
                  </Link>
                </h3>
                <p className="mt-4 max-w-prose text-[15.5px] leading-[1.8] text-muted" data-id="element-371" data-mp="2ojl2rmzj164y">
                  {place.hook}
                </p>

                <p className="mt-5 border-l-2 border-olive pl-5 text-[15px] leading-[1.75] text-ink/80" data-id="element-372" data-mp="32hrracil5try">
                  <span className="label mb-1.5 block text-olive" data-id="element-373" data-mp="1864nw1lmokzy">
                    Local tip
                  </span>
                  {place.insider}
                </p>

                <dl className="mt-7 grid grid-cols-4 gap-x-6 border-t border-line pt-5" data-id="element-374" data-mp="1394h1ogyfe7s">
                  {[['Best age', place.ages], ['Cost', place.cost], ['Visit', place.visit], ['Setting', place.setting]].map(([term, value]) => <div key={term} data-id="element-375" data-mp="3a3es84mkcmjt">
                      <dt className="label text-muted/70" data-id="element-376" data-mp="3a2c23yx2eo4y">{term}</dt>
                      <dd className="mt-1.5 text-[13.5px] leading-snug text-ink" data-id="element-377" data-mp="2w3cdgr9jy98s">
                        {value}
                      </dd>
                    </div>)}
                </dl>
              </li>)}
          </ul>
        </div>
      </section>

      <section aria-label="Sponsored message" className="bg-creme/60" data-id="element-378" data-mp="2r4j1j39wyqaq">
        <div className="mx-auto flex max-w-shell flex-wrap items-end justify-between gap-8 px-6 py-14 lg:px-10" data-id="element-379" data-mp="2r6vh59s78okm">
          <div className="max-w-xl" data-id="element-380" data-mp="l9zrxb0hezab">
            <p className="label text-ink/55" data-id="element-381" data-mp="pdpig60pvqxz">Sponsored by a local business</p>
            <h2 className="mt-4 font-display text-[30px] font-300 leading-tight text-ink" data-id="element-382" data-mp="1ya4ia73l049m">
              Zoo birthday parties, with the tent and the cleanup included
            </h2>
            <p className="mt-3 text-[15px] leading-[1.8] text-ink/70" data-id="element-383" data-mp="1p9oesxmu8x4r">
              Private party areas on the hillside lawn, admission for every
              guest, and a keeper visit. Weekend dates open six months ahead.
            </p>
          </div>
          <a href="#faq" className="label shrink-0 border border-ink/30 px-7 py-3.5 text-ink transition-colors duration-150 ease-soft hover:bg-ink hover:text-paper" data-id="element-384" data-mp="2gzkkcyux04s5">
            See party packages
          </a>
        </div>
      </section>

      <FilterBar filters={attractionsFilters} active={active} onToggle={toggle} data-id="element-385" data-mp="23a8yrx4hoyzu" />

      <section aria-labelledby="more-h" className="bg-paper" data-id="element-386" data-mp="30ol84jq228hx">
        <div className="mx-auto max-w-shell px-6 py-20 lg:px-10 lg:py-24" data-id="element-387" data-mp="30qxwpfqbjh3y">
          <TierNote tier="2" label="Rich rows" note="Everything else genuinely worth doing. Ten minutes to write, no page of its own." data-id="element-388" data-mp="2vxcypnoq6n4r" />
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-389" data-mp="389favz953k39">
            <h2 id="more-h" className="max-w-2xl font-display text-[38px] font-300 leading-[1.05] text-ink sm:text-[46px]" data-id="element-390" data-mp="3d7p8shm90ysk">
              More attractions worth the trip
            </h2>
            <p className="label text-muted" role="status" data-id="element-391" data-mp="3m73s8x40093k">
              {filtered.length}{' '}
              {filtered.length === 1 ? 'attraction' : 'attractions'}
              {active.length > 0 && ` · ${active.length} filters`}
            </p>
          </div>

          {filtered.length === 0 ? <div className="mt-14 max-w-lg" data-id="element-392" data-mp="4qc8w3aysw5i">
              <h3 className="font-display text-[30px] font-300 text-ink" data-id="element-393" data-mp="4tgnlum23aaf">
                Nothing matches all of that
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-394" data-mp="drk8qpt4q51p">
                Try dropping the neighborhood — most of these are within ten
                minutes of downtown anyway.
              </p>
              <button type="button" onClick={() => setActive([])} className="label mt-8 bg-ink px-7 py-3.5 text-paper transition-colors duration-150 ease-soft hover:bg-olive" data-id="element-395" data-mp="u3e4qsjv0z8m">
                Clear filters
              </button>
            </div> : <ul className="mt-12 border-t border-line" data-id="element-396" data-mp="gd2b2p7mmsfd">
              {filtered.map(place => <li key={place.id} data-id="element-397" data-mp="bfiv9kva67lb">
                  <article className="grid gap-y-5 border-b border-line py-9 lg:grid-cols-[1.6fr_1fr] lg:gap-x-16" data-id="element-398" data-mp="q2c5wctuup76">
                    <div data-id="element-399" data-mp="lblxeqq4u689">
                      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1" data-id="element-400" data-mp="laawcyo2ncku">
                        <h3 className="font-display text-[28px] font-300 leading-tight text-ink" data-id="element-401" data-mp="3tqe414f6p196">
                          {place.name}
                        </h3>
                        <span className="label text-olive" data-id="element-402" data-mp="24atmh6obnp7i">{place.area}</span>
                      </div>
                      <p className="mt-4 max-w-prose text-[15.5px] leading-[1.8] text-muted" data-id="element-403" data-mp="291s6ag8ir5fi">
                        {place.blurb}
                      </p>
                      <a href="#faq" className="label mt-5 inline-flex items-center gap-1.5 text-ink underline decoration-olive underline-offset-4" data-id="element-404" data-mp="1uaavzt295agm">
                        Official site
                        <ArrowUpRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-405" data-mp="2rz33lvalbosb" />
                      </a>
                    </div>
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-4 self-start" data-id="element-406" data-mp="2e03i2widjq0d">
                      {[['Cost', place.cost], ['Ages', place.ages], ['Time needed', place.duration], ['Setting', place.tags.includes('Indoor') ? 'Indoor' : 'Outdoor']].map(([term, value]) => <div key={term} data-id="element-407" data-mp="1lvrll47i62v2">
                          <dt className="label text-muted/70" data-id="element-408" data-mp="1qtt2pjldbs3w">{term}</dt>
                          <dd className="mt-1.5 text-[14px] text-ink" data-id="element-409" data-mp="1qvx865yx0z6a">{value}</dd>
                        </div>)}
                    </dl>
                  </article>
                </li>)}
            </ul>}
        </div>
      </section>

      <DirectoryColumns title="Everything else, by type" groups={attractionsTier3} data-id="element-410" data-mp="1336wzyyu6jx2" />
      <HubFaqs faqs={attractionsFaqs} data-id="element-411" data-mp="184w81em26xl5" />
      <SiblingHubs exclude={['Things to do']} data-id="element-412" data-mp="u4br6nzpfbua" />
    </main>;
}
function Comparison() {
  return <section aria-labelledby="compare-h" className="bg-foam/35" data-id="element-413" data-mp="y4vn5o8jk9eu">
      <div className="mx-auto max-w-shell px-6 py-20 lg:px-10 lg:py-24" data-id="element-414" data-mp="y7hxfvmth30r">
        <DesignNote label="Why this page opens with a table" note="Attractions are paid destinations you choose between, so the decision is comparative. A table answers it in one screen — and tables win featured snippets." data-id="element-415" data-mp="2oicq18v97yud" />
        <h2 id="compare-h" className="max-w-2xl font-display text-[38px] font-300 leading-[1.05] text-ink sm:text-[46px]" data-id="element-416" data-mp="curdv87il3yc">
          The short answer, in one table
        </h2>
        <p className="mt-5 max-w-prose text-[15.5px] leading-[1.8] text-ink/70" data-id="element-417" data-mp="luntylfu8uhs">
          Prices are for a family of four and were checked in August. Time
          needed is how long it actually holds up, not how long you could
          stay.
        </p>

        <div className="mt-14 overflow-x-auto" data-id="element-418" data-mp="vn3bxir4xj2o">
          <table className="w-full min-w-[820px] border-collapse text-left" data-id="element-419" data-mp="19l0dyuocyhoq">
            <caption className="sr-only" data-id="element-420" data-mp="14lx672gztxd2">
              Santa Barbara family attractions compared on price for a family of
              four, time needed, best age, indoor or outdoor, and whether a
              membership is worthwhile
            </caption>
            <thead data-id="element-421" data-mp="36ev2j89zb82r">
              <tr className="border-y border-ink/20" data-id="element-422" data-mp="36fenyyyu8elj">
                {['Attraction', 'Family of 4', 'Time needed', 'Best age', 'Setting', 'Membership'].map(h => <th key={h} scope="col" className="label py-4 pr-8 text-ink/60" data-id="element-423" data-mp="1kafy1ztmh2fu">
                    {h}
                  </th>)}
              </tr>
            </thead>
            <tbody data-id="element-424" data-mp="1t8hvy6i27eyw">
              {comparison.map(row => <tr key={row.name} className="border-b border-ink/12" data-id="element-425" data-mp="1y6t4mdwmbjfj">
                  <th scope="row" className="py-5 pr-8 font-display text-[21px] font-400 leading-snug text-ink" data-id="element-426" data-mp="22ytbkqb3vclm">
                    {row.name}
                  </th>
                  <td className="py-5 pr-8 text-[15px] text-ink" data-id="element-427" data-mp="2c0al1pu0cg1c">
                    {row.familyOf4}
                  </td>
                  <td className="py-5 pr-8 text-[14px] text-ink/75" data-id="element-428" data-mp="2gylwg4zyaylo">
                    {row.duration}
                  </td>
                  <td className="py-5 pr-8 text-[14px] text-ink/75" data-id="element-429" data-mp="58milcn0zt94">
                    {row.bestAge}
                  </td>
                  <td className="py-5 pr-8 text-[14px] text-ink/75" data-id="element-430" data-mp="3sf6pcv3avvr6">
                    {row.setting}
                  </td>
                  <td className="py-5 pr-8 text-[14px]" data-id="element-431" data-mp="3nhx1vkzh1u5z">
                    <span className={row.membershipGood ? 'text-ink' : 'text-ink/55'} data-id="element-432" data-mp="o18jwcgpcc13">
                      {row.membership}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </section>;
}